import React, { useState, useEffect } from 'react';
import { createRoot } from 'react-dom/client';

import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  FormGroup
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import Button from '@components/Button';
import theme from '@styles/theme';
import { makeStyles } from '@styles/makeStyles';

const useClasses = makeStyles()({
  root: { zIndex: `1500 !important` }
});

const AlertWrapper = ({
  callback = () => {},
  canCancel,
  cancelLabel = 'Cancel',
  checkboxLabel = '',
  instance,
  okColor = 'primary',
  okLabel = 'OK',
  requireConfirmationCheckbox = false,
  setStepNumber = () => undefined,
  stepNumber = 1,
  text,
  title,
  totalSteps = 1,
  onCancel = () => {}
}) => {
  const { classes } = useClasses();
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);

  useEffect(() => {
    setOpen(true);
  }, [instance]);

  const stateReset = () => {
    setOpen(false);
    setIsConfirmed(false);
    setStepNumber(1);
  };

  const onCancelHandler = () => {
    if (canCancel === false) return; // intentional disablement of the cancel handler
    stateReset();
    onCancel();
  };

  const onConfirm = async () => {
    setIsLoading(true);
    await callback();
    setIsLoading(false);

    if (stepNumber === totalSteps) {
      stateReset();
    } else {
      setStepNumber(stepNumber + 1);
    }
  };

  const dialogTitle = canCancel && !title ? 'Confirmation' : title;

  return (
    <Dialog open={open} onClose={onCancelHandler} classes={{ root: classes.root }}>
      {(title || canCancel) && <DialogTitle>{dialogTitle}</DialogTitle>}
      <DialogContent>
        <DialogContentText component="div">{text}</DialogContentText>
        {requireConfirmationCheckbox && (
          <FormGroup sx={{ mt: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isConfirmed}
                  onChange={event => {
                    setIsConfirmed(event.target.checked);
                  }}
                  inputProps={{ 'aria-label': 'controlled' }}
                />
              }
              label={checkboxLabel}
            />
          </FormGroup>
        )}
      </DialogContent>
      <DialogActions>
        {canCancel && (
          <Button disabled={isLoading} onClick={onCancelHandler}>
            {cancelLabel}
          </Button>
        )}
        <Button
          onClick={onConfirm}
          disabled={isLoading || (requireConfirmationCheckbox && !isConfirmed)}
          color={okColor}
          loading={isLoading}
        >
          {okLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

let modalRoot;

const confirmAlert = (text, callback, additionalProps = { stepNumber: 1 }) => {
  if (!modalRoot) {
    modalRoot = createRoot(document.getElementById('modalRoot'));
  }

  modalRoot.render(
    <ThemeProvider theme={theme}>
      <AlertWrapper
        text={text}
        callback={callback}
        canCancel
        instance={Math.random()}
        {...additionalProps}
      />
    </ThemeProvider>
  );
};

const standardAlert = (text, callback, additionalProps = {}) => {
  if (!modalRoot) {
    modalRoot = createRoot(document.getElementById('modalRoot'));
  }

  modalRoot.render(
    <ThemeProvider theme={theme}>
      <AlertWrapper
        text={text}
        callback={callback}
        instance={Math.random()}
        {...additionalProps}
      />
    </ThemeProvider>
  );
};

export { standardAlert, confirmAlert };
